import { useContext, useState, useCallback, useRef, useEffect } from 'react';
import ConfigContext from '@ubeya/shared-web/contexts/ConfigContext';
import useToaster from '@ubeya/shared/hooks/useToaster';
import * as api from '@ubeya/shared/services/api';
import { isImageFile, isVideoFile, getFileType } from '../helpDeskUtils';
import { MAX_ATTACHMENT_FILE_SIZE_BYTES, MAX_ATTACHMENT_FILE_SIZE_MB } from '../helpDeskConstants';

/**
 * Shared Help Desk attachment-upload state (preview list, parallel uploads, removal, object-URL
 * cleanup), reused by the message composer and the create-ticket modal.
 */
const useHelpDeskFileUpload = ({ accountId, onUploaded, onRemoved }) => {
  const { t } = useContext(ConfigContext);
  const { addError } = useToaster();

  const [pendingFiles, setPendingFiles] = useState([]);
  const [uploadingCount, setUploadingCount] = useState(0);
  const pendingFilesRef = useRef(pendingFiles);
  pendingFilesRef.current = pendingFiles;
  // Bumped on reset() to invalidate uploads already in flight, so their late results don't re-add
  // attachments or skew the uploading counter after the form has moved on.
  const uploadGenerationRef = useRef(0);

  const handleFileSelect = useCallback(
    async (event) => {
      const selectedFiles = Array.from(event.target.files || []);
      if (!selectedFiles.length) return;

      const files = selectedFiles.filter((file) => file.size <= MAX_ATTACHMENT_FILE_SIZE_BYTES);
      if (files.length < selectedFiles.length) {
        addError(t('help_desk_file_too_large', { maxSizeMb: MAX_ATTACHMENT_FILE_SIZE_MB }));
      }
      if (!files.length) {
        event.target.value = '';
        return;
      }

      const previews = files.map((file) => {
        return {
          id: `local-${Date.now()}-${Math.random()}`,
          name: file.name,
          size: file.size,
          isImage: isImageFile(file),
          isVideo: isVideoFile(file),
          type: getFileType(file),
          previewUrl: isImageFile(file) || isVideoFile(file) ? URL.createObjectURL(file) : null,
          uploading: true,
          uploadedData: null,
          file
        };
      });
      setPendingFiles((previous) => [
        ...previous,
        ...previews.map(({ file: _file, ...previewWithoutFile }) => previewWithoutFile)
      ]);
      setUploadingCount((count) => count + previews.length);

      const generation = uploadGenerationRef.current;
      await Promise.allSettled(
        previews.map(async ({ id, file }) => {
          try {
            const formData = new FormData();
            formData.append('file', file);
            const response = await api.uploadHelpDeskFile({ accountId, formData });
            // A reset() (e.g. employee/branch change) happened mid-upload — drop this stale result.
            if (uploadGenerationRef.current !== generation) return;
            const fileId = response?.data?.fileId ?? null;
            if (!fileId) {
              setPendingFiles((previous) => previous.filter((pendingFile) => pendingFile.id !== id));
              addError(t('help_desk_upload_failed'));
              return;
            }
            const uploadedData = { fileId };
            setPendingFiles((previous) =>
              previous.map((pendingFile) =>
                pendingFile.id === id ? { ...pendingFile, uploading: false, uploadedData } : pendingFile
              )
            );
            onUploaded?.(uploadedData);
          } catch {
            if (uploadGenerationRef.current !== generation) return;
            setPendingFiles((previous) => previous.filter((pendingFile) => pendingFile.id !== id));
            addError(t('help_desk_upload_failed'));
          } finally {
            // Only the generation that owns this upload adjusts the counter (reset() zeroes it).
            if (uploadGenerationRef.current === generation) {
              setUploadingCount((count) => count - 1);
            }
          }
        })
      );
      event.target.value = '';
    },
    [accountId, addError, t, onUploaded]
  );

  const removeFile = useCallback(
    (fileId) => {
      const removedFile = pendingFilesRef.current.find((pendingFile) => pendingFile.id === fileId);
      setPendingFiles((previous) => {
        const file = previous.find((pendingFile) => pendingFile.id === fileId);
        if (file?.previewUrl) URL.revokeObjectURL(file.previewUrl);
        return previous.filter((pendingFile) => pendingFile.id !== fileId);
      });
      if (removedFile?.uploadedData) onRemoved?.(removedFile.uploadedData);
    },
    [onRemoved]
  );

  const reset = useCallback(() => {
    // Invalidate in-flight uploads so their late results are ignored, then clear all upload state.
    uploadGenerationRef.current += 1;
    pendingFilesRef.current.forEach((pendingFile) => {
      if (pendingFile.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
    });
    setPendingFiles([]);
    setUploadingCount(0);
  }, []);

  useEffect(
    () => () => {
      pendingFilesRef.current.forEach((pendingFile) => {
        if (pendingFile.previewUrl) URL.revokeObjectURL(pendingFile.previewUrl);
      });
    },
    []
  );

  return { pendingFiles, uploadingCount, handleFileSelect, removeFile, reset };
};

export default useHelpDeskFileUpload;
